import { Box, Button, IconButton, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { BaseImage } from '~/components/common';
import styles from './Notes.module.scss';
import { MenuVerticalIcon } from '~/assets/icons';
import { useCallback, useEffect } from 'react';
import logo from '~/assets/images/logo.png';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import MenuOption from '~/components/jobs/menu-option/MenuOption';
import dayjs from 'dayjs';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetNoteList } from '~/thunks/job/jobThunk';
import EditNoteDialog from './edit-note-dialog/EditNoteDialog';
import { memo, useState } from 'react';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { IPayloadDeleteNotes } from '~/utils/interface/note';
import jobApi from '~/apis/job';
import { customerActions } from '~/thunks/customer/customerSlice';

const cx = classNames.bind(styles);

type NotesProps = {
  id: string;
  isContact: boolean;
};

const ActivitiesNotes = (props: NotesProps) => {
  // //#region Destructuring Props
  // //#endregion Destructuring Props

  // //#region Declare Hook
  const { t } = useTranslation('admin/customer');
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  // //#endregion Declare Hook

  // //#region Selector
  const { openAlertDelete, needRefreshData, notesList, menuSelectedId } = useAppSelector((state) => state.customer);
  // //#endregion Selector

  // //#region Declare State
  const [openEditNoteDialog, setOpenEditNoteDialog] = useState<boolean>(false);
  const [editNoteDialogMode, setEditNoteDialogMode] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dataEdit, setDataEdit] = useState<string>();
  // //#endregion Declare State

  // //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetNoteList({ customerId: props.id }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, needRefreshData]);

  //#endregion Implement Hook
  //#region Handle Function
  const handleClickCreateNote = async () => {
    setEditNoteDialogMode(DIALOG_MODE_CREATE);
    setOpenEditNoteDialog(true);
  };

  const handleCloseDialog = async (open: boolean, needRefreshData?: boolean) => {
    setEditNoteDialogMode('');
    setOpenEditNoteDialog(open);
  };

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId?: string) => {
    setAnchorEl(event.currentTarget);
    dispatch(customerActions.changeMenuSelectId(itemId));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditItem = () => {
    handleClose();
    setEditNoteDialogMode(DIALOG_MODE_EDIT);
    setOpenEditNoteDialog(true);
  };

  const handleDeleteItem = () => {
    handleClose();
    dispatch(customerActions.changeOpenAlertDelete(true));
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeOpenAlertDelete(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteSelectedJobs = useCallback(async () => {
    const ids = menuSelectedId ? [menuSelectedId] : [];
    const payloadDeleteJobs: IPayloadDeleteNotes = {
      ids: ids,
      customerId: props.id,
    };
    try {
      await jobApi.deleteNotes(payloadDeleteJobs);
      dispatch(customerActions.changeOpenAlertDelete(false));
      dispatch(customerActions.changeNeedRefreshData(true));
      notify('success', t('deleteSuccessMessage', { ns: 'admin/job' }));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuSelectedId, props.id, menuSelectedId]);

  //#endregion Handle Function
  return (
    <Box>
      <Box className='flex justify-between mt-[20px]'>
        <Box className={cx('label')}></Box>
        {permissions[PERMISSIONS.organizationNote_create] ? (
          <Button
            className='w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px] mt-[20px]'
            variant='contained'
            disableElevation
            onClick={handleClickCreateNote}
          >
            {t('activitiesNotes.addNote')}
          </Button>
        ) : (
          <></>
        )}
      </Box>
      {notesList.map((note, index) => (
        <Box key={index}>
          <Box className={cx('note-container')}>
            <Box className={cx('note-container-item')}>
              <BaseImage src={note.createdByAccount.companyAccount.avatar ?? logo} className={cx('avatar')} />
              <Box className={cx('user-name')}>{note.createdByAccount.companyAccount.name}</Box>
            </Box>

            <Box className={cx('note-container-item')}>
              <Typography className={cx('time')}>{dayjs(note.createdAt).format('MMM DD YYYY [at] h:mm a')}</Typography>
              <IconButton
                onClick={(event) => {
                  openOptionMenu(event, note.id);
                  setDataEdit(note.note);
                }}
              >
                <MenuVerticalIcon className='w-[18px] h-[18px]' />
              </IconButton>
            </Box>
          </Box>

          <Box className={cx('note')} dangerouslySetInnerHTML={{ __html: note.note }}></Box>
        </Box>
      ))}

      <EditNoteDialog
        open={openEditNoteDialog}
        mode={editNoteDialogMode}
        setOpen={handleCloseDialog}
        id={props.id}
        data={dataEdit}
      />

      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableDelete={permissions[PERMISSIONS.organizationNote_delete]}
        enableEdit={permissions[PERMISSIONS.organizationNote_edit]}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={
          props.isContact ? t('activitiesNotes.alertDescriptionContact') : t('activitiesNotes.alertDescription')
        }
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobs}
      />
    </Box>
  );
};

export default memo(ActivitiesNotes);
